import React, { useMemo, useState } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { putFetcher } from "@/lib/swr";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { GROUPS } from "@/constants/app-groups";
import type { App } from "@/types/domain";
import { GroupCombobox } from "./group-combobox";
import { refreshApps } from "./refresh-apps";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { useIsAdmin } from "@/hooks/auth";
// Edit name / group / app-mark / PRD key (owner or admin; the server re-checks). Apps
// are a fixed set — no delete here. Also reused by the admin app management
// page. `isApp` marks real client apps; unmarked = project-type entry — RC
// and other client-only app pickers narrow on it.
export const EditAppDialog = ({ app }: { app: App }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(app.name);
  const [group, setGroup] = useState(app.group || "");
  const [isApp, setIsApp] = useState(!!app.isApp);
  const [prdKey, setPrdKey] = useState(app.key || "");
  const { toast } = useToast();
  const isAdmin = useIsAdmin();
  // Short IDs already issued → key is baked into branches/PRs/docs; locked
  // (server enforces). Admin may still force it, with the warning below.
  const keyLocked = !!app.key && (app.prdSeq ?? 0) >= 1;
  const keyChanged = prdKey !== (app.key || "");
  const { data: apps } = useSWR<App[]>(["/apps"]);
  // Extensible options: canonical GROUPS ∪ groups already in use.
  const groupOptions = useMemo(() => {
    const set = new Set<string>(GROUPS);
    for (const a of apps ?? []) if (a.group) set.add(a.group);
    return [...set];
  }, [apps]);

  const { trigger: save, isMutating: saving } = useSWRMutation(
    `/apps/${app.id}`,
    putFetcher,
    {
      onSuccess: () => {
        refreshApps();
        setOpen(false);
      },
      // Key clashes / bad format come back as 400 with a readable message
      onError: (e) => toastError(toast, "Save failed", e),
    }
  );

  const onOpenChange = (o: boolean) => {
    if (o) {
      setName(app.name);
      setGroup(app.group || "");
      setIsApp(!!app.isApp);
      setPrdKey(app.key || "");
    }
    setOpen(o);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit app #{app.id}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <label className="block space-y-1">
            <span className="text-sm text-muted-foreground">Name</span>
            <Input
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
              className="h-8"
            />
          </label>
          <div className="space-y-1">
            <span className="text-sm text-muted-foreground">Group</span>
            <GroupCombobox
              value={group}
              onChange={setGroup}
              options={groupOptions}
            />
          </div>
          <label className="block space-y-1">
            <span className="text-sm text-muted-foreground">PRD key</span>
            <Input
              value={prdKey}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPrdKey(e.target.value.toUpperCase())
              }
              placeholder="e.g. CCP"
              maxLength={6}
              disabled={keyLocked && !isAdmin}
              className="h-8 font-mono"
            />
            <span className="block text-xs text-muted-foreground">
              {keyLocked
                ? "Locked: short IDs have been issued. Only an admin can change it."
                : <>2–6 letters/digits, starting with a letter; unique. PRDs get short IDs like{" "}
                  {prdKey || "CCP"}-42. Locked once the first short ID is issued.</>}
            </span>
          </label>
          {keyLocked && keyChanged && (
            <Alert variant="destructive">
              <AlertDescription>
                Existing short IDs ({app.key}-1…{app.key}-{app.prdSeq}) in
                branches, PRs, commits and docs will stop resolving. Old IDs
                are not kept.
              </AlertDescription>
            </Alert>
          )}
          <div className="space-y-1">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={isApp}
                onCheckedChange={(v) => setIsApp(v === true)}
              />
              <span>App</span>
            </label>
            <p className="text-xs text-muted-foreground">
              Marks a real client app (unmarked entries are project-type,
              e.g. backend/internal). Client-only features (e.g. RC) list
              only marked apps.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() =>
              save({
                name,
                group,
                isApp,
                // Only send the key when set/changed — an untouched empty key
                // stays unset
                ...(keyChanged
                  ? { key: prdKey, ...(keyLocked ? { force: true } : {}) }
                  : {}),
              })
            }
            disabled={saving}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
